/**
 * @fileoverview Person Profile Component with Error Handling
 * @active MAINTAINED - Used in person research pages
 *
 * This component renders a person's profile with integrated error handling using
 * the reusable ApiErrorAlert component. It provides graceful fallbacks for API failures
 * and data inconsistencies.
 *
 * WHEN TO USE:
 * - When displaying a person's profile data
 * - When data might be incomplete or unavailable due to API issues
 * - For research-focused person views
 *
 * @see {@link src/components/ui/error/ApiErrorAlert.tsx} - Error handling component
 * @see {@link src/components/personBy_Id/Profile_id.tsx} - Person profile component
 * @see {@link src/app/(main)/(home)/(research)/people/[person]/page.tsx} - Usage in route
 * @see {@link src/types/personAggregated.ts} - Person data structure definitions
 * @see {@link src/types/error.ts} - Error information structure
 */

"use client";

import React, { useEffect } from "react";

import { Box } from "@chakra-ui/react";

import Overview from "./overview/Overview";
import Profile_id from "./Profile_id";

import { BreadcrumbsBlock } from "@/components/ui/BreadcrumbsBlock";
import ApiErrorAlert from "@/components/ui/error/ApiErrorAlert";
import { MainWrapper } from "@/components/ui/box/MainWrapper";
import { TableInDetails } from "@/components/ui/tables/TableInDetails";
import { SourcesBottomText } from "@/components/ui/SourcesBottomText";
import { ExternalUrlIcons } from "@/components/ui/buttons/ExternalUrlIcons.server";
import { PersonData } from "@/types/personAggregated";
import { ErrorInfo } from "@/types/error";
import { useActions } from "@/hooks/useActions";

/**
 * Extended PersonData interface to include headline bio field
 * This allows strong typing while maintaining compatibility
 */
interface PersonWithBio extends PersonData {
  personHeadlineBio?: string;
}

/**
 * Tab configuration for person details
 */
const tabHeaders: string[] = ["Overview"];

interface PersonByIdProps {
  /** Person data to display, can be null if data fetch failed */
  person: PersonData | null;
  /** Whether the user is logged in (for freemium features) */
  isLogin: any;
}

/**
 * Component to display a person's profile with all related information
 *
 * The component renders:
 * 1. Breadcrumbs navigation
 * 2. Error alert if API error occurred
 * 3. Profile header with person details
 * 4. External URL/social media links
 * 5. Tabbed content section with Overview panel
 * 6. Sources attribution
 *
 * @param {PersonByIdProps} props - Component props
 * @returns {JSX.Element} The person profile component
 */
function PersonById({
  person: onePerson,
  isLogin,
}: PersonByIdProps): JSX.Element {
  const { setCompanyDetailsTabIndex } = useActions();

  useEffect(() => {
    setCompanyDetailsTabIndex(0);
  }, [setCompanyDetailsTabIndex]);

  // Early return for null person data
  if (!onePerson) {
    const error: ErrorInfo = {
      message: "Person data not found",
      type: "NOT_FOUND",
      canRetry: false,
    };
    return <ApiErrorAlert error={error} showGoBack={false} />;
  }

  // Log the person data to help debug name display issues
  console.log('PersonById received person data:', {
    id: onePerson?.id,
    fullName: onePerson?.fullName,
    firstName: onePerson?.firstName,
    lastName: onePerson?.lastName,
    nameFull: onePerson?.nameFull,
    nameFirst: onePerson?.nameFirst,
    nameLast: onePerson?.nameLast,
  });

  // Extract social media links from the person data
  const socialLinks = onePerson.socialMedia || {};

  // Cast the PersonData to PersonWithBio type for Profile_id component
  const personWithBio: PersonWithBio = onePerson as PersonWithBio;

  return (
    <>
      <MainWrapper
        customStyleOuter={{
          px: { base: "20px", md: "40px", lg: "40px" },
          mt: { base: "40px", md: "24px" },
        }}
        customStyleInner={{}}
      >
        <BreadcrumbsBlock
          arrayPath={["Home", "Research", "People", "data"]}
          objectName={
            onePerson.fullName || onePerson.nameFull || 
            [
              onePerson.firstName || onePerson.nameFirst, 
              onePerson.lastName || onePerson.nameLast
            ]
              ?.filter(Boolean)
              ?.join(" ") || "Person Profile"
          }
        />

        {/* Display API error information if present */}
        {onePerson._error && (
          <ApiErrorAlert error={onePerson._error} showGoBack={false} />
        )}
      </MainWrapper>
      <MainWrapper
        customStyleOuter={{
          px: { base: "20px", md: "40px" },
          mt: { base: "40px", md: "60px" },
        }}
        customStyleInner={{
          maxW: { base: "100%", md: "100%", xl: "1124px" },
          gap: "18px",
        }}
      >
        <Profile_id onePerson={personWithBio} />
        <ExternalUrlIcons data={socialLinks} />
      </MainWrapper>

      <Box mt={"30px"}>
        <TableInDetails
          dataObject={{ onePerson, isLogin }}
          tabsHeader={tabHeaders}
          tabsPanels={[Overview]}
        />
      </Box>

      <SourcesBottomText />
    </>
  );
}

export default PersonById;
